/**
 * Edits to a draft metadata model. Each operation changes the definition it is given in place;
 * the editor applies them to a fresh copy of the draft (structuredClone), so React sees a new object.
 */
import { fieldIdSlug, metadataTabs, newFieldIdProblem, sectionTabId, type FieldDefinition, type MetadataSchemaDefinition } from '@prism/contracts';

type Section = MetadataSchemaDefinition['sections'][number];

/** Where a dragged field or section would land: before/after another one, or at the end of a section (fields) or tab (sections). */
export type DropTarget =
  | { kind: 'field-before' | 'field-after'; id: string }
  | { kind: 'field-end'; sectionId: string }
  | { kind: 'section-before' | 'section-after'; id: string }
  | { kind: 'section-end'; tabId: string | null };

/** What hiding a field clears: it is no longer required, needed, in the inspector, a facet or a column. */
export const HIDE_FIELD: Partial<FieldDefinition> = { hidden: true, required: false, needed: undefined, inspector: undefined, facet: undefined, column: undefined };

/** Merge a patch into a field; keys set to undefined are removed. */
export function patchField(d: MetadataSchemaDefinition, id: string, patch: Partial<FieldDefinition>) {
  const f = d.fields.find((x) => x.id === id)!;
  Object.assign(f, patch);
  for (const k of Object.keys(patch) as Array<keyof FieldDefinition>) if (patch[k] === undefined) delete f[k];
}

/** Up or down among the fields of the same section. */
export function moveField(d: MetadataSchemaDefinition, id: string, dir: -1 | 1) {
  const f = d.fields.find((x) => x.id === id)!;
  const sameSection = d.fields.filter((x) => (x.section ?? d.sections[0]?.id) === (f.section ?? d.sections[0]?.id));
  const pos = sameSection.indexOf(f);
  const other = sameSection[pos + dir];
  if (!other) return;
  const a = d.fields.indexOf(f);
  const b = d.fields.indexOf(other);
  [d.fields[a], d.fields[b]] = [d.fields[b]!, d.fields[a]!];
}

/** The id the Add field dialog suggests for a label (fixes after rc.9 testing): f_ and the label's slug; empty without one. */
export function suggestFieldId(label: string): string {
  const slug = fieldIdSlug(label);
  return slug ? `f_${slug}` : '';
}

/**
 * Why the Add field dialog cannot use this id, from what the browser knows: its shape, the draft's fields, and the
 * published model's (a field removed in the draft is restored from Removed, not added again). The server checks the
 * rest of the workspace's history (GET /api/v1/metadata/field-ids/:id/available).
 */
export function draftFieldIdProblem(id: string, def: MetadataSchemaDefinition, published: MetadataSchemaDefinition): string | null {
  const shape = newFieldIdProblem(id, def.fields.map((f) => f.id));
  if (shape) return shape;
  if (published.fields.some((f) => f.id === id)) return 'A field removed in this draft has this id. Restore it from Removed instead';
  return null;
}

export function addField(d: MetadataSchemaDefinition, id: string, sectionId: string, label = 'New field') {
  d.fields.push({ id, label, type: 'text', required: false, section: sectionId });
}

export function removeField(d: MetadataSchemaDefinition, id: string) {
  d.fields = d.fields.filter((x) => x.id !== id);
}

export function addTab(d: MetadataSchemaDefinition, now = Date.now()) {
  const label = `Tab ${metadataTabs(d).length + 1}`;
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  d.tabs = [...(d.tabs ?? []), { id: (d.tabs ?? []).some((t) => t.id === base) ? `${base}_${now % 1000}` : base, label }];
}

export function moveTab(d: MetadataSchemaDefinition, id: string, dir: -1 | 1) {
  const list = d.tabs ?? [];
  const i = list.findIndex((t) => t.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j]!, list[i]!];
}

export function removeTab(d: MetadataSchemaDefinition, id: string) {
  d.tabs = (d.tabs ?? []).filter((t) => t.id !== id);
  if (!d.tabs.length) delete d.tabs; // no tabs = one implicit tab
}

/** A tab that still holds sections cannot be removed. */
export const tabInUse = (def: MetadataSchemaDefinition, id: string) => def.sections.some((s) => sectionTabId(def, s) === id);

/** Up or down among the sections of the same tab (the order of sections in other tabs is unaffected). */
export function moveSection(d: MetadataSchemaDefinition, id: string, dir: -1 | 1) {
  const i = d.sections.findIndex((x) => x.id === id);
  const tab = sectionTabId(d, d.sections[i]);
  let j = i + dir;
  while (j >= 0 && j < d.sections.length && sectionTabId(d, d.sections[j]) !== tab) j += dir;
  if (i < 0 || j < 0 || j >= d.sections.length) return;
  [d.sections[i], d.sections[j]] = [d.sections[j]!, d.sections[i]!];
}

export function addSection(d: MetadataSchemaDefinition, tabId?: string | null, now = Date.now()) {
  const label = `Section ${d.sections.length + 1}`;
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  d.sections.push({ id: d.sections.some((s) => s.id === id) ? `${id}_${now % 1000}` : id, label, ...(tabId ? { tab: tabId } : {}) });
}

export function removeSection(d: MetadataSchemaDefinition, id: string) {
  d.sections = d.sections.filter((x) => x.id !== id);
}

/** Put a field in a section: before or after another field, or last. */
export function dropFieldAt(d: MetadataSchemaDefinition, fieldId: string, target: DropTarget) {
  const f = d.fields.find((x) => x.id === fieldId);
  if (!f) return;
  const first = d.sections[0]?.id;
  const sectionOf = (x: FieldDefinition) => x.section ?? first;
  let sectionId: string | undefined;
  let anchor: FieldDefinition | undefined;
  let after = false;
  if (target.kind === 'field-before' || target.kind === 'field-after') {
    anchor = d.fields.find((x) => x.id === target.id);
    if (!anchor || anchor === f) return;
    sectionId = sectionOf(anchor);
    after = target.kind === 'field-after';
  } else if (target.kind === 'field-end') {
    sectionId = target.sectionId;
    anchor = d.fields.filter((x) => x !== f && sectionOf(x) === sectionId).at(-1);
    after = true;
  } else return;
  d.fields.splice(d.fields.indexOf(f), 1);
  f.section = sectionId;
  if (!anchor) d.fields.push(f);
  else d.fields.splice(d.fields.indexOf(anchor) + (after ? 1 : 0), 0, f);
}

/** Put a section in a tab: before or after another section, or last. */
export function dropSectionAt(d: MetadataSchemaDefinition, sectionId: string, target: DropTarget) {
  const s = d.sections.find((x) => x.id === sectionId);
  if (!s) return;
  let tabId: string | null;
  let anchor: Section | undefined;
  let after = false;
  if (target.kind === 'section-before' || target.kind === 'section-after') {
    anchor = d.sections.find((x) => x.id === target.id);
    if (!anchor || anchor === s) return;
    tabId = sectionTabId(d, anchor);
    after = target.kind === 'section-after';
  } else if (target.kind === 'section-end') {
    tabId = target.tabId;
    anchor = d.sections.filter((x) => x !== s && sectionTabId(d, x) === tabId).at(-1);
    after = true;
  } else return;
  d.sections.splice(d.sections.indexOf(s), 1);
  if (tabId) s.tab = tabId; else delete s.tab;
  if (!anchor) d.sections.push(s);
  else d.sections.splice(d.sections.indexOf(anchor) + (after ? 1 : 0), 0, s);
}
